import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getJobs, getLocations, deleteJob, getPrices, getBuildings } from "../lib/api";
import { photoSrc, STATUSES, CATEGORIES, TRIWULAN_OPTIONS, triwulanLabel, formatRp, jobCost } from "../lib/constants";
import { exportWord, exportExcel, printReport, copyTable } from "../lib/exports";
import { StatusBadge, CategoryBadge, KondisiBadge } from "./Badges";
import { JobFormModal } from "./JobFormModal";
import { LocationsManager } from "./LocationsManager";
import { BuildingsManager } from "./BuildingsManager";
import { SettingsEditor } from "./SettingsEditor";
import { PriceManager } from "./PriceManager";
import { CostSummary } from "./CostSummary";
import { BulkTriwulanModal } from "./BulkTriwulanModal";
import { CopyTriwulanModal } from "./CopyTriwulanModal";
import { AiSummaryModal } from "./AiSummaryModal";
import { ImportExcelModal } from "./ImportExcelModal";
import { ReportPreviewModal } from "./ReportPreviewModal";
import { MaintenanceSchedulePanel } from "./MaintenanceSchedulePanel";
import { DashboardCharts } from "./DashboardCharts";
import { Lightbox } from "./Lightbox";

const StatCard = ({ label, value, icon, accent }) => (
  <div className="relative bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className={`absolute left-0 top-0 h-full w-1 ${accent.bar}`} />
    <div className="flex items-center justify-between pl-1.5">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
        <h3 className="font-display text-3xl font-extrabold mt-1 text-slate-900 tracking-tight tabular-nums">{value}</h3>
      </div>
      <div className={`p-3 rounded-xl ring-1 ${accent.iconBg} ${accent.iconText} ${accent.ring}`}><i className={`ph-bold ${icon} text-2xl`} /></div>
    </div>
  </div>
);

export default function Dashboard({ session, settings, onHome, onLogout, onSettingsChanged }) {
  const isAdmin = session.role === "admin";
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [lokasi, setLokasi] = useState("ALL");
  const [triwulan, setTriwulan] = useState("ALL");
  const [gedung, setGedung] = useState("ALL");
  const [showJobForm, setShowJobForm] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [showLocations, setShowLocations] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPrices, setShowPrices] = useState(false);
  const [showCost, setShowCost] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [showBuildings, setShowBuildings] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    try {
      setJobs(await getJobs());
      if (isAdmin) { setLocations(await getLocations()); setPrices(await getPrices()); setBuildings(await getBuildings()); }
    } catch { toast.error("Gagal memuat data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const all = jobs.flatMap((j) => j.items);
    const gedungSet = new Set();
    jobs.forEach((j) => j.items.forEach((it) => gedungSet.add(`${j.location_id}|${it.gedung || j.title}`)));
    return {
      total: all.length,
      gedung: gedungSet.size,
      selesai: all.filter((i) => i.status === "Selesai").length,
      proses: all.filter((i) => i.status !== "Selesai").length,
    };
  }, [jobs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs
      .filter((j) => lokasi === "ALL" || j.location_id === lokasi)
      .filter((j) => triwulan === "ALL" || String(j.triwulan || "") === triwulan)
      .map((j) => {
        const txt = `${j.location_name} ${j.title}`.toLowerCase();
        const items = j.items.filter((it) => {
          if (category !== "ALL" && it.kategori !== category) return false;
          if (status !== "ALL" && it.status !== status) return false;
          if (gedung !== "ALL" && (it.gedung || j.title) !== gedung) return false;
          if (q && !txt.includes(q) && !`${it.gedung} ${it.ruang} ${it.spesifikasi} ${it.detail} ${it.bahan}`.toLowerCase().includes(q)) return false;
          return true;
        });
        return { ...j, items };
      })
      .filter((j) => j.items.length > 0);
  }, [jobs, search, category, status, lokasi, triwulan, gedung]);

  const gedungOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => j.items.forEach((it) => { if (it.gedung) set.add(it.gedung); }));
    return Array.from(set).sort();
  }, [jobs]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const j of filtered) {
      const key = j.triwulan ? `${j.triwulan}|${j.tahun || ""}` : "none";
      if (!map.has(key)) map.set(key, { key, triwulan: j.triwulan || 0, tahun: j.tahun || 0, label: triwulanLabel(j), jobs: [] });
      map.get(key).jobs.push(j);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.triwulan === 0) return 1;
      if (b.triwulan === 0) return -1;
      if (b.tahun !== a.tahun) return b.tahun - a.tahun;
      return a.triwulan - b.triwulan;
    });
  }, [filtered]);

  const onDelete = async (job) => {
    if (!window.confirm(`Hapus pekerjaan "${job.title}"?`)) return;
    try { await deleteJob(job.id); toast.success("Data dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const ItemRow = ({ item }) => {
    const photos = item.photos || [];
    const loc = [item.gedung, item.lantai, item.ruang].filter(Boolean).join(" • ");
    return (
      <div className="p-2.5 bg-slate-50/90 rounded-xl border border-slate-200 flex items-start gap-3 justify-between">
        <div className="space-y-1.5 flex-1 text-xs">
          <div className="flex items-center gap-1.5 flex-wrap"><CategoryBadge kategori={item.kategori} /><KondisiBadge kondisi={item.kondisi} /><StatusBadge status={item.status} /></div>
          {loc && <div className="flex items-center gap-1 text-slate-500 font-semibold"><i className="ph-bold ph-map-pin text-slate-400" />{loc}</div>}
          {item.spesifikasi && <div><span className="font-bold text-slate-800">Spesifikasi: </span><span className="text-slate-700">{item.spesifikasi}</span></div>}
          {item.kendala && <div><span className="font-bold text-rose-700">Kendala: </span><span className="text-slate-700">{item.kendala}</span></div>}
          {item.detail && <div><span className="font-bold text-teal-700">Pengerjaan: </span><span className="text-slate-600">{item.detail}</span></div>}
          {item.bahan && item.bahan !== "-" && <div><span className="font-bold text-orange-700">Bahan: </span><span className="text-slate-600">{item.bahan}</span></div>}
        </div>
        {photos.length > 0 && (
          <div className="flex gap-1.5 shrink-0 flex-wrap max-w-[9rem] justify-end">
            {photos.map((p, i) => (
              <img key={i} src={photoSrc(p)} alt="" onClick={() => setLightbox(photoSrc(p))}
                className="w-12 h-12 rounded-lg object-cover cursor-pointer hover:opacity-80 border border-slate-300 shadow-sm" />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col pb-12 bg-slate-100">
      <header className="no-print sticky top-0 z-30 bg-slate-900/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {settings?.logo_url ? (
              <img src={photoSrc(settings.logo_url)} alt="" className="h-9 w-auto object-contain" />
            ) : (
              <div className="p-2 bg-teal-500/15 text-teal-300 rounded-xl ring-1 ring-teal-400/30"><i className="ph-bold ph-gauge text-xl sm:text-2xl" /></div>
            )}
            <div className="min-w-0">
              <h1 className="font-display text-white text-sm sm:text-lg leading-tight truncate">{settings?.company_name || "Dashboard"}</h1>
              <p className="text-[10px] sm:text-xs text-teal-300/90 font-semibold">
                {isAdmin ? "Mode Admin — Akses Penuh" : `Pelanggan: ${session.location_name}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {isAdmin && (
              <>
                <button onClick={() => { setEditJob(null); setShowJobForm(true); }} className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-400 text-slate-900 px-3 py-2 rounded-lg text-xs font-extrabold" data-testid="add-job-btn"><i className="ph-bold ph-plus-circle text-base" /><span className="hidden sm:inline">Pekerjaan</span></button>
                <button onClick={() => setShowLocations(true)} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="manage-loc-btn"><i className="ph-bold ph-map-pin-line text-base" /><span className="hidden sm:inline">Lokasi</span></button>
                <button onClick={() => setShowBuildings(true)} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="manage-buildings-btn"><i className="ph-bold ph-buildings text-base" /><span className="hidden sm:inline">Gedung</span></button>
                <button onClick={() => setShowImport(true)} className="inline-flex items-center gap-1.5 bg-emerald-500/90 hover:bg-emerald-500 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="import-excel-btn"><i className="ph-bold ph-microsoft-excel-logo text-base" /><span className="hidden sm:inline">Import Excel</span></button>
                <button onClick={() => setShowPrices(true)} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="manage-prices-btn"><i className="ph-bold ph-tag text-base" /><span className="hidden sm:inline">Harga</span></button>
                <button onClick={() => setShowCost(true)} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="cek-biaya-btn"><i className="ph-bold ph-calculator text-base" /><span className="hidden sm:inline">Cek Biaya</span></button>
                <button onClick={() => setShowAi(true)} className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="ai-summary-btn"><i className="ph-bold ph-sparkle text-base" /><span className="hidden sm:inline">Ringkasan AI</span></button>
                <button onClick={() => setShowBulk(true)} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="bulk-triwulan-btn"><i className="ph-bold ph-calendar-plus text-base" /><span className="hidden sm:inline">Isi Triwulan</span></button>
                <button onClick={() => setShowCopy(true)} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="copy-triwulan-btn"><i className="ph-bold ph-copy-simple text-base" /><span className="hidden sm:inline">Salin Triwulan</span></button>
                <button onClick={() => setShowSettings(true)} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="edit-landing-btn"><i className="ph-bold ph-layout text-base" /><span className="hidden sm:inline">Landing</span></button>
              </>
            )}
            <button onClick={onHome} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs font-bold"><i className="ph-bold ph-house text-base" /><span className="hidden sm:inline">Beranda</span></button>
            <button onClick={onLogout} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="logout-btn"><i className="ph-bold ph-sign-out text-base" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 flex-1 w-full">
        <section className="no-print grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <StatCard label="Total Pekerjaan" value={stats.total} icon="ph-list-checks" accent={{ bar: "bg-slate-900", iconBg: "bg-slate-100", iconText: "text-slate-700", ring: "ring-slate-200" }} />
          <StatCard label="Total Gedung" value={stats.gedung} icon="ph-buildings" accent={{ bar: "bg-teal-600", iconBg: "bg-teal-50", iconText: "text-teal-700", ring: "ring-teal-200" }} />
          <StatCard label="Selesai" value={stats.selesai} icon="ph-check-circle" accent={{ bar: "bg-emerald-600", iconBg: "bg-emerald-50", iconText: "text-emerald-700", ring: "ring-emerald-200" }} />
          <StatCard label="Proses / Belum" value={stats.proses} icon="ph-clock-afternoon" accent={{ bar: "bg-orange-600", iconBg: "bg-orange-50", iconText: "text-orange-700", ring: "ring-orange-200" }} />
        </section>

        <DashboardCharts
          jobs={jobs}
          onSelectLokasi={isAdmin ? (id) => { setLokasi(id); setCategory("ALL"); setStatus("ALL"); setSearch(""); } : undefined}
          onSelectStatus={(st) => { setStatus(st); setCategory("ALL"); setSearch(""); }}
          onSelectTriwulan={(t) => { setTriwulan(t); setCategory("ALL"); setStatus("ALL"); setSearch(""); }}
        />

        {isAdmin && <MaintenanceSchedulePanel buildings={buildings} jobs={jobs} waNumber={settings?.whatsapp} />}

        <section className="no-print bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm mb-6 flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="relative flex-1">
              <i className="ph-bold ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari lokasi, judul, atau kendala..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-teal-500 outline-none" data-testid="search-input" />
            </div>
            {isAdmin && (
              <select value={lokasi} onChange={(e) => setLokasi(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-xs sm:text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500" data-testid="filter-lokasi">
                <option value="ALL">Semua Lokasi</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-xs sm:text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500" data-testid="filter-kategori">
              <option value="ALL">Semua Kategori</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={triwulan} onChange={(e) => setTriwulan(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-xs sm:text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500" data-testid="filter-triwulan">
              <option value="ALL">Semua Triwulan</option>
              {TRIWULAN_OPTIONS.map((t) => <option key={t} value={String(t)}>Triwulan {t}</option>)}
            </select>
            {gedungOptions.length > 0 && (
              <select value={gedung} onChange={(e) => setGedung(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-xs sm:text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500" data-testid="filter-gedung">
                <option value="ALL">Semua Gedung</option>
                {gedungOptions.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            )}
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-xs sm:text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500" data-testid="filter-status">
              <option value="ALL">Semua Status</option>
              {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="preview-report-btn"><i className="ph-bold ph-file-magnifying-glass text-base" /> Pratinjau & Unduh</button>
            <button onClick={async () => { await copyTable(filtered); toast.success("Tabel disalin"); }} className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="copy-btn"><i className="ph-bold ph-copy text-base" /> Salin</button>
            <button onClick={printReport} className="inline-flex items-center gap-1.5 bg-teal-700 hover:bg-teal-800 text-white px-3 py-2 rounded-lg text-xs font-bold" data-testid="print-btn"><i className="ph-bold ph-printer text-base" /> Cetak</button>
          </div>
        </section>

        {loading ? (
          <div className="no-print py-20 text-center text-slate-400"><i className="ph-bold ph-circle-notch text-3xl animate-spin" /><p className="text-sm mt-2">Memuat data...</p></div>
        ) : filtered.length === 0 ? (
          <div className="no-print py-16 text-center bg-white rounded-xl border border-slate-200/80 shadow-sm" data-testid="empty-state">
            <div className="inline-flex p-4 rounded-full bg-slate-100 text-slate-400 mb-3"><i className="ph-bold ph-clipboard-text text-4xl" /></div>
            <h3 className="text-base font-semibold text-slate-700">Belum ada data pekerjaan</h3>
            <p className="text-xs text-slate-500 mt-1">{isAdmin ? "Tambahkan pekerjaan baru atau sesuaikan filter." : "Belum ada riwayat pekerjaan untuk lokasi Anda."}</p>
          </div>
        ) : (
          <section className="no-print space-y-6" data-testid="jobs-list">
            {groups.map((group) => (
              <div key={group.key} className="space-y-3" data-testid={`triwulan-group-${group.triwulan}`}>
                <div className="flex items-center gap-2 pt-1">
                  <span className="inline-flex items-center gap-1.5 text-sm font-extrabold text-white bg-slate-900 px-3 py-1.5 rounded-lg">
                    <i className="ph-bold ph-calendar-check text-teal-400" />{group.label}
                  </span>
                  <span className="text-[11px] font-semibold text-slate-400">{group.jobs.length} pekerjaan</span>
                  <div className="flex-1 h-px bg-slate-200" />
                  <button onClick={() => exportWord(group.jobs, settings?.company_name, settings?.logo_url)} className="inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-lg text-[11px] font-bold" data-testid={`group-word-${group.triwulan}`}><i className="ph-bold ph-file-doc" /> Word</button>
                  <button onClick={() => exportExcel(group.jobs)} className="inline-flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-lg text-[11px] font-bold" data-testid={`group-excel-${group.triwulan}`}><i className="ph-bold ph-file-xls" /> Excel</button>
                </div>
                {group.jobs.map((job) => (
                  <div key={job.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3 animate-fade-up">
                    <div className="flex justify-between items-start gap-3 border-b pb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                          {isAdmin && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded"><i className="ph-bold ph-map-pin" />{job.location_name}</span>}
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded"><i className="ph-bold ph-calendar-blank" />{triwulanLabel(job)}</span>
                        </div>
                        <h4 className="font-extrabold text-slate-800 text-base truncate">{job.title}</h4>
                        <p className="text-[11px] text-slate-400 font-semibold">{job.items.length} item{isAdmin ? ` • Estimasi: ${formatRp(jobCost(job))}` : ""}</p>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1.5 shrink-0">
                          <button onClick={() => exportWord([job], settings?.company_name, settings?.logo_url)} className="px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-bold" data-testid={`job-word-${job.id}`}><i className="ph-bold ph-file-doc" /></button>
                          <button onClick={() => exportExcel([job])} className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold" data-testid={`job-excel-${job.id}`}><i className="ph-bold ph-file-xls" /></button>
                          <button onClick={() => { setEditJob(job); setShowJobForm(true); }} className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold" data-testid={`edit-job-${job.id}`}><i className="ph-bold ph-pencil-simple" /> Edit</button>
                          <button onClick={() => onDelete(job)} className="px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg text-xs font-bold" data-testid={`delete-job-${job.id}`}><i className="ph-bold ph-trash" /> Hapus</button>
                        </div>
                      )}
                      {!isAdmin && (
                        <div className="flex gap-1.5 shrink-0">
                          <button onClick={() => exportWord([job], settings?.company_name, settings?.logo_url)} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-bold" data-testid={`job-word-${job.id}`}><i className="ph-bold ph-file-doc" /> Word</button>
                          <button onClick={() => exportExcel([job])} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold" data-testid={`job-excel-${job.id}`}><i className="ph-bold ph-file-xls" /> Excel</button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">{job.items.map((item, i) => <ItemRow key={i} item={item} />)}</div>
                  </div>
                ))}
              </div>
            ))}
          </section>
        )}

        <section className="print-only">
          {settings?.logo_url && <img src={photoSrc(settings.logo_url)} alt="" className="h-14 mx-auto mb-2 object-contain" />}
          <h1 className="text-xl font-bold text-center uppercase mb-1">Laporan Pekerjaan Service</h1>
          <p className="text-center text-xs text-gray-600 mb-4">{settings?.company_name}</p>
          <table className="w-full border-collapse border border-gray-400 text-xs">
            <thead><tr className="bg-gray-100">
              <th className="border border-gray-400 p-2">Gedung</th><th className="border border-gray-400 p-2">Lantai</th>
              <th className="border border-gray-400 p-2">Ruang</th><th className="border border-gray-400 p-2">Kategori</th>
              <th className="border border-gray-400 p-2">Kondisi</th>
              <th className="border border-gray-400 p-2">Spesifikasi</th><th className="border border-gray-400 p-2">Pengerjaan</th>
              <th className="border border-gray-400 p-2">Bahan</th><th className="border border-gray-400 p-2">Status</th>
            </tr></thead>
            <tbody>
              {filtered.flatMap((job) => job.items.map((item, i) => (
                <tr key={`${job.id}-${i}`}>
                  <td className="border border-gray-400 p-2 font-bold">{item.gedung || job.title}</td>
                  <td className="border border-gray-400 p-2">{item.lantai}</td>
                  <td className="border border-gray-400 p-2">{item.ruang}</td>
                  <td className="border border-gray-400 p-2">{item.kategori}</td>
                  <td className="border border-gray-400 p-2">{item.kondisi}</td>
                  <td className="border border-gray-400 p-2">{item.spesifikasi}</td>
                  <td className="border border-gray-400 p-2">{item.detail}</td>
                  <td className="border border-gray-400 p-2">{item.bahan}</td>
                  <td className="border border-gray-400 p-2 font-bold">{item.status}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </section>
      </main>

      {isAdmin && <>
        <JobFormModal open={showJobForm} initialJob={editJob} locations={locations} buildings={buildings} prices={prices} onClose={() => setShowJobForm(false)} onSaved={() => { setShowJobForm(false); load(); }} />
        <LocationsManager open={showLocations} onClose={() => setShowLocations(false)} onChanged={load} />
        <BuildingsManager open={showBuildings} onClose={() => setShowBuildings(false)} onChanged={load} />
        <ImportExcelModal open={showImport} onClose={() => setShowImport(false)} onChanged={load} />
        <PriceManager open={showPrices} onClose={() => setShowPrices(false)} onChanged={() => getPrices().then(setPrices)} />
        <CostSummary open={showCost} jobs={filtered} onClose={() => setShowCost(false)} />
        <BulkTriwulanModal open={showBulk} jobs={jobs} onClose={() => setShowBulk(false)} onChanged={load} />
        <CopyTriwulanModal open={showCopy} jobs={jobs} onClose={() => setShowCopy(false)} onChanged={load} />
        <AiSummaryModal
          open={showAi}
          title={lokasi !== "ALL" ? (locations.find((l) => l.id === lokasi)?.name || "") : (gedung !== "ALL" ? gedung : "Semua Data")}
          items={filtered.flatMap((j) => j.items.map((it) => ({ gedung: it.gedung || j.title, ruang: it.ruang, kategori: it.kategori, spesifikasi: it.spesifikasi, kondisi: it.kondisi, status: it.status })))}
          waNumber={settings?.whatsapp}
          onClose={() => setShowAi(false)}
        />
        <SettingsEditor open={showSettings} settings={settings} onClose={() => setShowSettings(false)} onSaved={(s) => { setShowSettings(false); onSettingsChanged && onSettingsChanged(s); }} />
      </>}
      <ReportPreviewModal open={showPreview} jobs={filtered} company={settings?.company_name} logoUrl={settings?.logo_url} onClose={() => setShowPreview(false)} />
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
